// src/components/sampleorders/SampleOrderForm.js
// ✅ FIX: Removes render loop that could freeze nav (memoized items + derived lockedItemIndices)
// ✅ FIX: approvalEnabled uses autoApproveUnder != null (handles undefined correctly)
// ✅ UI UPDATE: "Auto-approved" success + "Estimated Order Total" summary are now hidden behind a ✅ check icon
//             at the BOTTOM (next to Submit). The icon only appears when the order WILL auto-approve.
// ✅ NEW: Quantity validation - prevents selecting more than available inventory quantity

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Package,
  Send,
  UserCheck,
  Plus,
  Trash2,
  Calendar,
  Building2,
  Lock,
  Check,
  Info,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import DeliveryDatePicker from "./DeliveryDatePicker";
import { getKitScope } from "../../utils/sampleKitPermissions";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normLower = (v) => String(v ?? "").trim().toLowerCase();

const getAccountDisplayName = (acc = {}) => acc.name || acc.accountName || "Untitled account";
const getAssignedInternalUserId = (acc = {}) =>
  acc.assignedToId || acc.assignedUserId || acc.repUserId || acc.salesRepId || acc.userId || null;

const SampleOrderForm = ({
  form,
  inventory = [],
  companyUsers,
  accounts,
  kits = [],
  saving,
  onSubmit,
  onChange,
  deliveryDays,
  sampleRequestLeadDays,
  isAdmin = false,
  currentUserId = null,
  companySettings = null,
  embedded = false,
  onCancel = null,
  title = "New Sample Order",
  description = "Build one sample order with multiple line items or use a prebuilt kit.",

  // ✅ accept userProfile (was being used but not always passed)
  userProfile = null,
  resetKey = 0,
}) => {
  const EMPTY_NEW_CUSTOMER = useMemo(
    () => ({
      name: "",
      accountName: "",
      email: "",
      phone: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
    }),
    []
  );
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState(EMPTY_NEW_CUSTOMER);

  // Helpers to track kit matching
  const [autoDetectedKitId, setAutoDetectedKitId] = useState(null);
  const [isManualKitSelection, setIsManualKitSelection] = useState(false);

  // State for approval info tooltip (non-admin icon)
  const [showApprovalTooltip, setShowApprovalTooltip] = useState(false);

  // ✅ Auto-approve summary tooltip (admin-only)
  const [showAutoApproveTooltip, setShowAutoApproveTooltip] = useState(false);
  const [itemSearchTerms, setItemSearchTerms] = useState([]);
  const [activeItemSearchIndex, setActiveItemSearchIndex] = useState(null);

  // --- Helpers for account display ------------------------------------------
  const getAccountStreet = (acc = {}) => {
    if (acc.address && typeof acc.address === "object") {
      return acc.address.street || acc.address.line1 || null;
    }
    return acc.addressStreet || acc.street || null;
  };

  const getAccountCity = (acc = {}) => {
    if (acc.address && typeof acc.address === "object") {
      return acc.address.city || null;
    }
    return acc.addressCity || acc.city || null;
  };

  const getAccountState = (acc = {}) => {
    if (acc.address && typeof acc.address === "object") {
      return acc.address.state || null;
    }
    return acc.addressState || acc.state || null;
  };

  const getAccountZip = (acc = {}) => {
    if (acc.address && typeof acc.address === "object") {
      return acc.address.zip || acc.address.postalCode || null;
    }
    return acc.addressZip || acc.zip || null;
  };

  // --- Active users (exclude inactive/deactivated) ---------------------------
  const activeUsers =
    (companyUsers || []).filter(
      (u) => u && u.active !== false && u.status !== "Inactive" && u.status !== "Deactivated"
    ) || [];

  // ✅ resolve currentUser using userProfile first
  const currentUser =
    userProfile || activeUsers.find((u) => String(u.id) === String(currentUserId)) || null;

  const currentRole = normLower(currentUser?.role);
  const isAdminOrOwner = isAdmin || currentRole === "owner" || currentRole === "admin";

  const accountsForDropdown = useMemo(() => {
    const list = Array.isArray(accounts) ? [...accounts] : [];

    if (isAdminOrOwner) {
      return list.sort((a, b) =>
        getAccountDisplayName(a).toLowerCase().localeCompare(getAccountDisplayName(b).toLowerCase())
      );
    }

    const myUid = String(currentUser?.uid || currentUser?.id || currentUserId || "").trim();
    const myAccountIds = Array.from(
      new Set(
        []
          .concat(
            Array.isArray(currentUser?.customerAccountIds) ? currentUser.customerAccountIds : [],
            Array.isArray(currentUser?.activeCustomerMembership?.accountIds)
              ? currentUser.activeCustomerMembership.accountIds
              : [],
            Array.isArray(userProfile?.customerAccountIds) ? userProfile.customerAccountIds : [],
            Array.isArray(userProfile?.activeCustomerMembership?.accountIds)
              ? userProfile.activeCustomerMembership.accountIds
              : [],
            currentUser?.activeCustomerMembership?.primaryAccountId || [],
            currentUser?.activeCustomerMembership?.accountId || [],
            currentUser?.accountId || [],
            userProfile?.activeCustomerMembership?.primaryAccountId || [],
            userProfile?.activeCustomerMembership?.accountId || [],
            userProfile?.accountId || []
          )
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
    const filtered = list.filter((acc) => {
      if (!acc?.id) return false;

      if (currentRole === "customer") {
        return myAccountIds.includes(String(acc.id));
      }

      const createdBy = String(acc?.createdBy || "").trim();
      const assignedToId = String(getAssignedInternalUserId(acc) || "").trim();
      const sharedUserIds = []
        .concat(safeArray(acc.sharedWithUserIds))
        .concat(safeArray(acc.visibleToUserIds))
        .concat(safeArray(acc.endUserIds))
        .concat(safeArray(acc.customerUserIds))
        .concat(safeArray(acc.portalUserIds))
        .filter(Boolean)
        .map(String);

      const singleSharedId = String(
        acc.customerUserId ||
          acc.endUserId ||
          acc.assignedEndUserId ||
          acc.portalUserId ||
          acc.visibleToUserId ||
          ""
      ).trim();

      if (myUid && (createdBy === myUid || assignedToId === myUid)) return true;
      if (myUid && (sharedUserIds.includes(myUid) || (singleSharedId && singleSharedId === myUid))) return true;
      return false;
    });

    filtered.sort((a, b) =>
      getAccountDisplayName(a).toLowerCase().localeCompare(getAccountDisplayName(b).toLowerCase())
    );

    return filtered;
  }, [accounts, currentRole, currentUser, currentUserId, isAdminOrOwner, userProfile]);

  // ✅ If an end user somehow has a hidden/admin-only account selected, clear it
  useEffect(() => {
    if (isAdminOrOwner) return;
    if (showNewCustomer) return;

    const id = String(form.accountId || "").trim();
    if (!id) return;

    const stillVisible = accountsForDropdown.some((a) => a.id === id);
    if (!stillVisible) {
      onChange("accountId", "");
      onChange("accountName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrOwner, showNewCustomer, form.accountId, accountsForDropdown]);

  // ✅ SECURITY: ONLY owner/admin can see pricing
  const canSeePricing = isAdminOrOwner;
  const canAssignOthers = isAdminOrOwner;
  const requireReasonForSample = !!companySettings?.featureFlags?.requireReasonForSample;

  // For non-admins, default Assign To to the logged in user
  useEffect(() => {
    if (!canAssignOthers && currentUser && !form.assignedToId) {
      onChange("assignedToId", currentUser.id);
    }
  }, [canAssignOthers, currentUser, form.assignedToId, onChange]);

  // ✅ NEW: Build inventory map for quick lookups (including available quantity)
  const inventoryMap = useMemo(() => {
    const map = new Map();
    (inventory || []).forEach((inv) => {
      if (inv?.id) {
        // Check both 'quantity' and 'stock' fields for available quantity
        const availableQty = 
          typeof inv.quantity === "number" ? inv.quantity :
          typeof inv.stock === "number" ? inv.stock :
          0;
        
        map.set(inv.id, {
          ...inv,
          availableQty,
        });
      }
    });
    return map;
  }, [inventory]);

  const getInventoryOptionLabel = useCallback((inv) => {
    if (!inv) return "";
    return `${inv.name || "Unnamed product"}${inv.sku ? ` (${inv.sku})` : ""}`;
  }, []);

  const searchableInventory = useMemo(() => {
    return [...(inventory || [])].sort((a, b) =>
      getInventoryOptionLabel(a).toLowerCase().localeCompare(getInventoryOptionLabel(b).toLowerCase())
    );
  }, [inventory, getInventoryOptionLabel]);

  // ✅ NEW: Helper to get max available quantity for a product
  const getMaxQuantity = useCallback(
    (productId) => {
      const product = inventoryMap.get(productId);
      if (!product) return 0;

      // Sample orders must always leave at least 1 unit in inventory.
      return Math.max(0, (Number(product.availableQty) || 0) - 1);
    },
    [inventoryMap]
  );

  // --- Line items helpers ----------------------------------------------------
  // ✅ FIX: memoize items so it's not a brand new array each render (prevents render loops)
  const items = useMemo(() => {
    const raw = Array.isArray(form.items) ? form.items : [];
    if (raw.length > 0) return raw;

    return [
      {
        productId: form.productId || "",
        quantity: form.quantity || 1,
      },
    ];
  }, [form.items, form.productId, form.quantity]);

  useEffect(() => {
    setItemSearchTerms((prev) =>
      items.map((item, index) => {
        if (!item?.productId) return "";
        return getInventoryOptionLabel(inventoryMap.get(item.productId));
      })
    );
  }, [items, inventoryMap, getInventoryOptionLabel]);

  const handleItemsChange = useCallback(
    (updatedItems) => {
      onChange("items", updatedItems);

      if (updatedItems.length > 0) {
        const first = updatedItems[0];
        onChange("productId", first.productId || "");
        onChange("quantity", first.quantity || 1);
      } else {
        onChange("productId", "");
        onChange("quantity", 1);
      }
    },
    [onChange]
  );

  const handleItemChange = useCallback(
    (index, field, value) => {
      const updated = [...items];
      const current = updated[index] || { productId: "", quantity: 1 };
      
      if (field === "quantity") {
        // ✅ NEW: Enforce max quantity based on inventory
        const productId = current.productId;
        if (productId) {
          const maxQty = getMaxQuantity(productId);
          const requestedQty = value === "" ? "" : Number(value) || 1;
          
          // Cap the quantity at available inventory
          if (typeof requestedQty === "number") {
            value = Math.min(requestedQty, maxQty);
          }
        }
      }
      
      updated[index] = {
        ...current,
        [field]: field === "quantity" ? (value === "" ? "" : Number(value) || 1) : value,
      };
      handleItemsChange(updated);
    },
    [items, handleItemsChange, getMaxQuantity]
  );

  const handleAddItem = useCallback(() => {
    const updated = [...items, { productId: "", quantity: 1 }];
    handleItemsChange(updated);
  }, [items, handleItemsChange]);

  const getFilteredInventoryOptions = useCallback(
    (query) => {
      const needle = normLower(query);
      if (!needle) return searchableInventory.slice(0, 25);
      return searchableInventory
        .filter((inv) => {
          const haystack = `${inv.name || ""} ${inv.sku || ""}`;
          return normLower(haystack).includes(needle);
        })
        .slice(0, 50);
    },
    [searchableInventory]
  );

  const handleItemSearchChange = useCallback(
    (index, value) => {
      setItemSearchTerms((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });

      const selectedLabel = getInventoryOptionLabel(inventoryMap.get(items[index]?.productId));
      if (items[index]?.productId && value !== selectedLabel) {
        handleItemChange(index, "productId", "");
      }

      setActiveItemSearchIndex(index);
    },
    [getInventoryOptionLabel, handleItemChange, inventoryMap, items]
  );

  const handleInventoryOptionPick = useCallback(
    (index, inv) => {
      handleItemChange(index, "productId", inv.id);
      setItemSearchTerms((prev) => {
        const next = [...prev];
        next[index] = getInventoryOptionLabel(inv);
        return next;
      });
      setActiveItemSearchIndex(null);
    },
    [getInventoryOptionLabel, handleItemChange]
  );

  // ✅ NEW: Check if any items exceed available inventory
  const inventoryViolations = useMemo(() => {
    const violations = [];
    items.forEach((item, index) => {
      if (!item.productId) return;
      
      const product = inventoryMap.get(item.productId);
      if (!product) return;
      
      const requestedQty = Number(item.quantity) || 0;
      const availableQty = product.availableQty || 0;
      const maxSampleQty = Math.max(0, availableQty - 1);
      
      if (availableQty <= 1 || requestedQty > maxSampleQty) {
        violations.push({
          index,
          productName: product.name || "Unknown product",
          requested: requestedQty,
          available: availableQty,
          maxSampleQty,
        });
      }
    });
    return violations;
  }, [items, inventoryMap]);

  const hasInventoryViolations = inventoryViolations.length > 0;

  const hasPrimaryProductSelection = Boolean(items[0]?.productId);

  // ===========================================================================
  // ✅ Guardrails (autoApproveUnder only)
  // ===========================================================================
  const guardrails = companySettings?.featureFlags || {};
  const requireApprovalForAdmins = guardrails.requireApprovalForAdmins === true;

  const maxItemsPerOrder =
    typeof guardrails.maxItemsPerOrder === "number" ? guardrails.maxItemsPerOrder : null;

  // ✅ FIX: approvalEnabled should not be true when autoApproveUnder is undefined
  const autoApproveUnder =
    typeof guardrails.autoApproveUnder === "number" ? guardrails.autoApproveUnder : null;
  const approvalEnabled = autoApproveUnder != null;
  const adminMustRequireApproval = isAdminOrOwner && requireApprovalForAdmins;

  const validItemCount = useMemo(
    () => items.filter((i) => i.productId && Number(i.quantity) > 0).length,
    [items]
  );

  const itemLimitExceeded = maxItemsPerOrder !== null && validItemCount > maxItemsPerOrder;
  const addDisabled = maxItemsPerOrder !== null && items.length >= maxItemsPerOrder;

  // ✅ ADMIN ONLY: pricing calc
  const inventoryById = useMemo(() => {
    if (!canSeePricing) return new Map();
    const m = new Map();
    (inventory || []).forEach((inv) => {
      if (inv?.id) m.set(inv.id, inv);
    });
    return m;
  }, [inventory, canSeePricing]);

  const { estimatedTotal, pricingIncomplete } = useMemo(() => {
    if (!canSeePricing) return { estimatedTotal: 0, pricingIncomplete: false };

    let total = 0;
    let missing = false;

    items.forEach((item) => {
      if (!item?.productId || !item?.quantity) return;

      const qty = Number(item.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return;

      const product = inventoryById.get(item.productId) || null;
      if (!product) return;

      const rawPrice = product.price ?? product.unitPrice ?? product.samplePrice ?? product.cost ?? null;

      if (typeof rawPrice !== "number" || !Number.isFinite(rawPrice)) {
        missing = true;
        return;
      }

      total += rawPrice * qty;
    });

    return { estimatedTotal: total, pricingIncomplete: missing };
  }, [items, inventoryById, canSeePricing]);

  const formatCurrency = (amount) => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(Number(amount || 0));
    } catch {
      return `$${Number(amount || 0).toFixed(2)}`;
    }
  };

  const willNeedApproval = useMemo(() => {
    if (adminMustRequireApproval) return true;
    if (!canSeePricing || !approvalEnabled) return false;
    return pricingIncomplete || (autoApproveUnder != null && estimatedTotal > autoApproveUnder);
  }, [adminMustRequireApproval, canSeePricing, approvalEnabled, pricingIncomplete, autoApproveUnder, estimatedTotal]);

  const willAutoApprove = useMemo(() => {
    if (adminMustRequireApproval) return false;
    if (!canSeePricing || !approvalEnabled) return false;
    if (pricingIncomplete) return false;
    if (autoApproveUnder == null) return false;
    return estimatedTotal <= autoApproveUnder;
  }, [adminMustRequireApproval, canSeePricing, approvalEnabled, pricingIncomplete, autoApproveUnder, estimatedTotal]);

  // Show approval icon for non-admin users when approval is enabled
  const showApprovalIcon = !canSeePricing && approvalEnabled;

  // ✅ Icon only appears when the order WILL auto-approve (per your request)
  const showAutoApproveIcon = canSeePricing && approvalEnabled && willAutoApprove;

  // --- Kits ------------------------------------------------------------------
  const selectedKit =
    form.kitId && kits?.length ? kits.find((k) => k.id === form.kitId) || null : null;

  // ✅ FIX: derive locked indices (no state + no effect = no render loop)
  const lockedItemIndices = useMemo(() => {
    if (!selectedKit?.items || !Array.isArray(selectedKit.items) || selectedKit.items.length === 0) {
      return new Set();
    }

    const kitProductIds = new Set(selectedKit.items.map((i) => i?.productId).filter(Boolean));
    const locked = new Set();

    items.forEach((item, index) => {
      if (kitProductIds.has(item?.productId)) locked.add(index);
    });

    return locked;
  }, [selectedKit, items]);

  // Helper function to check if current items match a kit
  const checkKitMatch = useCallback(() => {
    if (items.length === 0 || kits.length === 0) return null;

    const validItems = items.filter((item) => item.productId);
    if (validItems.length === 0) return null;

    const currentProductIds = validItems.map((item) => item.productId).sort();

    for (const kit of kits) {
      if (!kit.items || !Array.isArray(kit.items) || kit.items.length === 0) continue;

      const kitProductIds = kit.items.map((kitItem) => kitItem.productId).filter(Boolean).sort();

      if (
        currentProductIds.length === kitProductIds.length &&
        currentProductIds.every((id, index) => id === kitProductIds[index])
      ) {
        return kit.id;
      }
    }

    return null;
  }, [items, kits]);

  // Auto-detect kit when items change
  useEffect(() => {
    if (isManualKitSelection) return;

    const matchingKitId = checkKitMatch();

    if (matchingKitId && matchingKitId !== form.kitId) {
      setAutoDetectedKitId(matchingKitId);
      onChange("kitId", matchingKitId);
    } else if (!matchingKitId && form.kitId && autoDetectedKitId === form.kitId) {
      setAutoDetectedKitId(null);
      onChange("kitId", "");
    }
  }, [items, kits, form.kitId, autoDetectedKitId, isManualKitSelection, checkKitMatch, onChange]);

  const handleKitChange = (value) => {
    setIsManualKitSelection(true);

    if (!value) {
      onChange("kitId", "");
      setAutoDetectedKitId(null);
      return;
    }

    const kit = kits.find((k) => k.id === value) || null;
    onChange("kitId", value);
    setAutoDetectedKitId(null);

    if (kit && Array.isArray(kit.items) && kit.items.length > 0) {
      const mappedItems = kit.items.map((kitItem) => ({
        productId: kitItem.productId || "",
        quantity: typeof kitItem.quantity === "number" && kitItem.quantity > 0 ? kitItem.quantity : 1,
      }));
      handleItemsChange(mappedItems);
    }
  };

  // Reset manual selection flag after a delay
  useEffect(() => {
    if (!isManualKitSelection) return;
    const timer = setTimeout(() => setIsManualKitSelection(false), 5000);
    return () => clearTimeout(timer);
  }, [isManualKitSelection]);

  // Close auto-approve tooltip if it no longer applies
  useEffect(() => {
    if (!showAutoApproveIcon && showAutoApproveTooltip) {
      setShowAutoApproveTooltip(false);
    }
  }, [showAutoApproveIcon, showAutoApproveTooltip]);

  useEffect(() => {
    setShowNewCustomer(false);
    setNewCustomer(EMPTY_NEW_CUSTOMER);
    setAutoDetectedKitId(null);
    setIsManualKitSelection(false);
    setShowApprovalTooltip(false);
    setShowAutoApproveTooltip(false);
    setItemSearchTerms([]);
    setActiveItemSearchIndex(null);
  }, [EMPTY_NEW_CUSTOMER, resetKey]);

  // --- Account / customer helpers -------------------------------------------
  const handleAccountChange = (value) => {
    if (value === "new") {
      setShowNewCustomer(true);
      onChange("accountId", "");
      onChange("accountName", "");
    } else {
      setShowNewCustomer(false);
      const selectedAccount = (accountsForDropdown || []).find((acc) => acc.id === value) || null;
      onChange("accountId", value);
      onChange("accountName", selectedAccount?.name || selectedAccount?.accountName || "");
    }
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomer((prev) => ({ ...prev, [field]: value }));
    if (field === "name") onChange("accountName", value);
  };

  const isNewCustomerValid = () =>
    newCustomer.name.trim() &&
    newCustomer.accountName.trim() &&
    newCustomer.email.trim() &&
    newCustomer.phone.trim() &&
    newCustomer.addressStreet.trim() &&
    newCustomer.addressCity.trim() &&
    newCustomer.addressState.trim() &&
    newCustomer.addressZip.trim();

  const selectedAccount =
    !showNewCustomer && form.accountId
      ? (accountsForDropdown || []).find((acc) => acc.id === form.accountId) || null
      : null;

  // ===========================================================================
  // Guardrail Warnings (success + estimated total moved to bottom ✅ icon)
  // ===========================================================================
  const getGuardrailWarnings = () => {
    const warnings = [];

    // ✅ NEW: Inventory violations take priority
    if (hasInventoryViolations) {
      inventoryViolations.forEach((violation) => {
        warnings.push({
          type: "error",
          message:
            violation.available <= 1
              ? `${violation.productName}: Only ${violation.available} left in inventory. Samples must leave at least 1 in stock.`
              : `${violation.productName}: Requested ${violation.requested}, but samples can use at most ${violation.maxSampleQty} when ${violation.available} are in inventory.`,
        });
      });
    }

    if (itemLimitExceeded) {
      warnings.push({
        type: "error",
        message: `This order has ${validItemCount} items, but the limit is ${maxItemsPerOrder} items per order.`,
      });
    }

    if (canSeePricing && approvalEnabled) {
      if (pricingIncomplete) {
        warnings.push({
          type: "warning",
          message: "Some items are missing a price, so this order will be sent for admin approval.",
        });
      }

      if (adminMustRequireApproval) {
        warnings.push({
          type: "warning",
          message: "Admins and owners must submit this order for approval under current company settings.",
        });
      } else if (willNeedApproval && !pricingIncomplete) {
        warnings.push({
          type: "warning",
          message: `This order (${formatCurrency(estimatedTotal)}) is over ${formatCurrency(
            autoApproveUnder
          )} and will require admin approval.`,
        });
      }

      // ✅ removed the success message here (now behind ✅ icon)
    }

    return warnings;
  };

  const guardrailWarnings = getGuardrailWarnings();

  // --- Line items actions ----------------------------------------------------
  const handleRemoveItem = (index) => {
    if (lockedItemIndices.has(index)) return;

    if (items.length === 1) {
      handleItemsChange([{ productId: "", quantity: 1 }]);
      return;
    }
    const updated = items.filter((_, i) => i !== index);
    handleItemsChange(updated);
  };

  // --- Submit ----------------------------------------------------------------
  const handleLocalSubmit = (e) => {
    e.preventDefault();

    // ✅ NEW: Block submission if inventory violations exist
    if (hasInventoryViolations) {
      alert(
        `Cannot submit order. Samples must always leave at least 1 item in inventory:\n\n${inventoryViolations
          .map((v) =>
            v.available <= 1
              ? `• ${v.productName}: Only ${v.available} left in inventory, so it cannot be sampled right now.`
              : `• ${v.productName}: Requested ${v.requested}, but the sample max is ${v.maxSampleQty} with ${v.available} on hand.`
          )
          .join("\n")}\n\nPlease adjust quantities to match available inventory.`
      );
      return;
    }

    if (itemLimitExceeded) {
      alert(
        `Cannot submit order. You have ${validItemCount} items, but the maximum allowed is ${maxItemsPerOrder} items per order. Please remove some items.`
      );
      return;
    }

    // If not new customer, ensure selected account is visible (extra safety)
    if (!showNewCustomer && form.accountId && !isAdminOrOwner) {
      const ok = accountsForDropdown.some((a) => a.id === form.accountId);
      if (!ok) {
        alert("Please select a valid customer account.");
        onChange("accountId", "");
        onChange("accountName", "");
        return;
      }
    }

    const shippingAddress = showNewCustomer
      ? [newCustomer.addressStreet, newCustomer.addressCity, newCustomer.addressState, newCustomer.addressZip]
          .filter(Boolean)
          .join(", ")
      : null;

    onSubmit(e, {
      isNewCustomer: showNewCustomer,
      newCustomer: showNewCustomer ? { ...newCustomer, shippingAddress } : null,
      groupMode: validItemCount > 1 ? "grouped" : "single",
      kitId: form.kitId || null,
    });
  };

  // Check if current items match any kit for UI display
  const matchingKitId = checkKitMatch();
  const isAutoMatched = matchingKitId && matchingKitId === form.kitId && !isManualKitSelection;
  const submitLabel = saving
    ? "Submitting..."
    : hasInventoryViolations
    ? "Fix inventory issues to continue"
    : itemLimitExceeded && maxItemsPerOrder !== null
    ? `Remove ${validItemCount - maxItemsPerOrder} item(s) to continue`
    : validItemCount > 1
    ? `Submit Sample Order (${validItemCount} items)`
    : "Submit Sample Order";
  const sectionShellClass = "space-y-2";
  const sectionHeaderClass = "flex items-center gap-2 border-b border-gray-200 pb-1";

  // --- Render ----------------------------------------------------------------
  const renderedForm = inventory.length === 0 ? (
    <div className={embedded ? "min-h-0 flex-1 overflow-y-auto p-3 sm:p-6" : "flex-1 overflow-y-auto p-4"}>
      <div className="text-center py-12">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-600 font-medium">No sampleable inventory items available</p>
        <p className="text-xs text-gray-500 mt-1">
          Mark items as &quot;Sample-able&quot; in Inventory management
        </p>
      </div>
    </div>
  ) : (
    <form
      id={embedded ? "sample-order-create-form" : undefined}
      className="flex h-full min-h-0 flex-col"
      onSubmit={handleLocalSubmit}
    >
      <div className={embedded ? "min-h-0 flex-1 overflow-y-auto p-3 sm:p-6" : "min-h-0 flex-1 overflow-y-auto p-4"}>
        <div className="space-y-4">
            {/* SECTION: Sample Items + Kits */}
            <div className={sectionShellClass}>
              <div className="flex flex-col gap-2 border-b border-gray-200 pb-1 md:flex-row md:items-center md:justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Package className="w-4 h-4 text-blue-600" />
                  Sample Items
                </label>

                {kits?.length > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-600">Apply kit:</span>
                    <select
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={form.kitId || ""}
                      onChange={(e) => handleKitChange(e.target.value)}
                    >
                      <option value="">None</option>
                      {kits.map((kit) => (
                        <option key={kit.id} value={kit.id}>
                          {(kit.name || "Untitled kit") +
                            (getKitScope(kit) === "company" ? " • Default" : " • Personal")}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {selectedKit && (
                <div className="text-[11px] text-gray-600 bg-blue-50 border border-blue-100 rounded px-3 py-1.5 flex items-center gap-2">
                  <Lock className="w-3 h-3 text-blue-600" />
                  {isAutoMatched ? (
                    <>
                      <Check className="w-3 h-3 text-green-600" />
                      <span className="font-medium text-green-700">Auto-matched kit:</span>
                    </>
                  ) : (
                    <span className="font-medium text-blue-800">Using kit:</span>
                  )}{" "}
                  {selectedKit.name || "Untitled kit"} •{" "}
                  {Array.isArray(selectedKit.items) ? `${selectedKit.items.length} item(s)` : "Items not defined"}
                  {isAutoMatched && (
                    <span className="ml-2 text-[10px] text-gray-500 italic">(items match this kit - auto-tagged)</span>
                  )}
                </div>
              )}

              {/* Auto-match notification */}
              {matchingKitId && !form.kitId && (
                <div className="text-[11px] text-gray-600 bg-green-50 border border-green-100 rounded px-3 py-1.5 flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>
                    Your items match the{" "}
                    <span className="font-medium text-green-700">
                      {kits.find((k) => k.id === matchingKitId)?.name || "Unknown kit"}
                    </span>{" "}
                    kit. Select it above to apply kit pricing and restrictions.
                  </span>
                </div>
              )}

              {/* Column headers */}
              <div className="grid grid-cols-[1fr_90px_40px] gap-2 text-[11px] text-gray-500 px-1">
                <span>Product</span>
                <span>Qty</span>
                <span></span>
              </div>

              {/* Line items */}
              <div className="space-y-1">
                {items.map((item, index) => {
                  const isLocked = lockedItemIndices.has(index);
                  const product = item.productId ? inventoryMap.get(item.productId) : null;
                  const maxQty = item.productId ? getMaxQuantity(item.productId) : 999;
                  const availableQty = Number(product?.availableQty || 0);
                  const hasQtyIssue =
                    !!product && (availableQty <= 1 || Number(item.quantity) > maxQty);
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="grid grid-cols-[1fr_90px_40px] gap-2 items-start">
                        <div className="relative">
                          <input
                            type="text"
                            className={`w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${
                              isLocked ? "bg-gray-50" : ""
                            }`}
                            value={itemSearchTerms[index] ?? ""}
                            onChange={(e) => handleItemSearchChange(index, e.target.value)}
                            onFocus={() => !isLocked && setActiveItemSearchIndex(index)}
                            onBlur={() => window.setTimeout(() => setActiveItemSearchIndex(null), 120)}
                            placeholder="Search product or SKU"
                            required={index === 0 && !item.productId}
                            disabled={isLocked}
                          />
                          {activeItemSearchIndex === index && !isLocked ? (
                            <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                              {(itemSearchTerms[index] || "").trim().length === 0 ? (
                                <div className="px-3 py-2 text-xs text-gray-500">
                                  Type to search by product name or SKU. Showing first 25 items.
                                </div>
                              ) : null}
                              {getFilteredInventoryOptions(itemSearchTerms[index] || "").length ? (
                                getFilteredInventoryOptions(itemSearchTerms[index] || "").map((inv) => {
                                  const unavailable = getMaxQuantity(inv.id) < 1;
                                  return (
                                    <button
                                      key={inv.id}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => !unavailable && handleInventoryOptionPick(index, inv)}
                                      disabled={unavailable}
                                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
                                        unavailable
                                          ? "cursor-not-allowed bg-gray-50 text-gray-400"
                                          : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                      }`}
                                    >
                                      <span className="min-w-0 truncate">{getInventoryOptionLabel(inv)}</span>
                                      {unavailable ? (
                                        <span className="shrink-0 text-[11px] text-amber-600">Unavailable</span>
                                      ) : null}
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="px-3 py-2 text-xs text-gray-500">No inventory matches that search.</div>
                              )}
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <input
                            type="number"
                            min="1"
                            max={maxQty}
                            className={`w-full rounded-md border px-3 py-1.5 text-sm focus:ring-2 ${
                              hasQtyIssue
                                ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            } ${isLocked ? "bg-gray-50" : ""}`}
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            required={index === 0}
                            disabled={isLocked || (!!product && maxQty < 1)}
                            title={product ? `Max available: ${product.availableQty || 0}` : ""}
                          />
                        </div>

                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            disabled={isLocked}
                            className={`w-full h-[32px] flex items-center justify-center rounded-md transition-colors ${
                              isLocked
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                            }`}
                            aria-label={isLocked ? "Item locked (from kit)" : "Remove item"}
                            title={isLocked ? "This item is from a kit and cannot be removed" : "Remove item"}
                          >
                            {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      {/* ✅ NEW: Show available quantity warning */}
                      {hasQtyIssue && (
                        <div className="text-[11px] text-red-600 flex items-center gap-1 px-1">
                          <AlertTriangle className="w-3 h-3" />
                          {availableQty <= 1
                            ? `Only ${availableQty} left in inventory. Samples must leave at least 1 in stock.`
                            : `Only ${maxQty} can be sampled right now (${availableQty} in inventory).`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pt-1">
                {hasPrimaryProductSelection ? (
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={addDisabled}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    <Plus className="w-4 h-4" />
                    Add another item to this shipment
                  </button>
                ) : null}

                {addDisabled && maxItemsPerOrder !== null && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    You&apos;ve reached the maximum of {maxItemsPerOrder} item(s) for this order.
                  </div>
                )}

                {lockedItemIndices.size > 0 && (
                  <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Items with lock icon are from the selected kit and cannot be removed
                  </div>
                )}
              </div>
            </div>

            {/* Guardrail Warnings (only error/warning/success, not info) */}
            {guardrailWarnings.length > 0 && (
              <div className="space-y-1.5">
                {guardrailWarnings.map((warning, idx) => (
                  <div
                    key={idx}
                    className={`rounded px-3 py-2 text-sm flex items-start gap-3 ${
                      warning.type === "error"
                        ? "bg-red-50 border border-red-200 text-red-800"
                        : warning.type === "warning"
                        ? "bg-amber-50 border border-amber-200 text-amber-800"
                        : "bg-emerald-50 border border-emerald-200 text-emerald-800"
                    }`}
                  >
                    <span className="text-lg leading-none">
                      {warning.type === "error" ? "🚫" : warning.type === "warning" ? "⚠️" : "✅"}
                    </span>
                    <p className="flex-1">{warning.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* SECTION: Customer & Assignment */}
            <div className={sectionShellClass}>
              <div className={sectionHeaderClass}>
                <Building2 className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-gray-900">Customer & Assignment</h4>
              </div>

              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {/* Account select */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Account / Customer</label>
                  <select
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    value={showNewCustomer ? "new" : form.accountId || ""}
                    onChange={(e) => handleAccountChange(e.target.value)}
                    required
                  >
                    <option value="">Select customer</option>
                    <option value="new" className="text-blue-600">
                      + New Customer
                    </option>
                    {accountsForDropdown.map((account) => (
                      <option key={account.id} value={account.id}>
                        {getAccountDisplayName(account)}
                      </option>
                    ))}
                  </select>

                  {!isAdminOrOwner && accountsForDropdown.length === 0 && (
                    <div className="mt-2 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
                      No accounts are available for your user.
                    </div>
                  )}

                  {!showNewCustomer && selectedAccount && (
                    <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700 space-y-0.5">
                      <div className="font-medium text-gray-900">{getAccountDisplayName(selectedAccount)}</div>
                      {selectedAccount.contact && <div>Contact: {selectedAccount.contact}</div>}
                      {selectedAccount.email && <div>Email: {selectedAccount.email}</div>}
                      {selectedAccount.phone && <div>Phone: {selectedAccount.phone}</div>}
                      {(getAccountStreet(selectedAccount) ||
                        getAccountCity(selectedAccount) ||
                        getAccountState(selectedAccount) ||
                        getAccountZip(selectedAccount)) && (
                        <div className="text-gray-600">
                          {[
                            getAccountStreet(selectedAccount),
                            getAccountCity(selectedAccount),
                            getAccountState(selectedAccount),
                            getAccountZip(selectedAccount),
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Assignment */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Assign To{" "}
                    <span className="text-gray-500 text-[11px] ml-1">
                      ({canAssignOthers ? "optional" : "locked to you"})
                    </span>
                  </label>

                  {canAssignOthers ? (
                    <select
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      value={form.assignedToId || ""}
                      onChange={(e) => onChange("assignedToId", e.target.value)}
                    >
                      <option value="">Not assigned</option>
                      {activeUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName || user.email} ({user.role})
                        </option>
                      ))}
                    </select>
                  ) : currentUser ? (
                    <div className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-900">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      <span>
                        {currentUser.fullName || currentUser.email}{" "}
                        {currentUser.role && <span className="text-xs text-gray-500">({currentUser.role})</span>}
                      </span>
                    </div>
                  ) : (
                    <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
                      Assignment will be set by an admin.
                    </div>
                  )}
                </div>
              </div>

              {/* New Customer Form */}
              {showNewCustomer && (
                <div className="mt-2 space-y-2 rounded border border-blue-200 bg-blue-50 p-2.5">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-xs text-gray-900 flex items-center gap-2">
                      <Plus className="w-3.5 h-3.5 text-blue-600" />
                      New Customer Details
                    </h4>
                    <span className="text-[11px] text-gray-500">Simple contact + address for shipping.</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                          Contact Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={newCustomer.name}
                          onChange={(e) => handleNewCustomerChange("name", e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={newCustomer.email}
                          onChange={(e) => handleNewCustomerChange("email", e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                          Account / Restaurant Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={newCustomer.accountName}
                          onChange={(e) => handleNewCustomerChange("accountName", e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={newCustomer.phone}
                          onChange={(e) => handleNewCustomerChange("phone", e.target.value)}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="col-span-2">
                          <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                            Street <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            value={newCustomer.addressStreet}
                            onChange={(e) => handleNewCustomerChange("addressStreet", e.target.value)}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            value={newCustomer.addressCity}
                            onChange={(e) => handleNewCustomerChange("addressCity", e.target.value)}
                            required
                          />
                        </div>

                        <div className="grid grid-cols-[1fr_1fr] gap-1.5">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                              State <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              value={newCustomer.addressState}
                              onChange={(e) => handleNewCustomerChange("addressState", e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-0.5">
                              ZIP <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              value={newCustomer.addressZip}
                              onChange={(e) => handleNewCustomerChange("addressZip", e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION: Scheduling */}
            <div className={sectionShellClass}>
              <div className={sectionHeaderClass}>
                <Calendar className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-gray-900">Scheduling</h4>
              </div>

              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                <div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
                      Requested Delivery Date
                    </label>
                    <div className="flex-1 min-w-0">
                      <DeliveryDatePicker
                        value={form.requestedDate}
                        onChange={(dateStr) => onChange("requestedDate", dateStr)}
                        deliveryDays={deliveryDays}
                        leadDays={sampleRequestLeadDays}
                        hideLabel={true}
                      />
                    </div>
                  </div>
                </div>

                {isAdmin && form.status === "Approved" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Actual Delivery Date <span className="text-gray-500 text-xs ml-1">(admin only)</span>
                    </label>
                    <div className="space-y-1">
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.deliveryDate || ""}
                        onChange={(e) => onChange("deliveryDate", e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => onChange("deliveryDate", form.requestedDate || "")}
                        disabled={!form.requestedDate}
                        className="w-full text-xs font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        Use same date as requested
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SECTION: Additional Information */}
            <div className={sectionShellClass}>
              <div className={sectionHeaderClass}>
                <Package className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-gray-900">Order Notes</h4>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {requireReasonForSample ? "Reason for Sample" : "Notes (Optional)"}
                  </label>
                  <textarea
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    value={form.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder={
                      requireReasonForSample
                        ? "Explain why this sample is needed..."
                        : "Special instructions or requirements..."
                    }
                  />
                  {requireReasonForSample ? (
                    <p className="mt-1 text-[11px] text-amber-700">
                      A reason is required before this sample request can be submitted.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button with Icons */}
        <div
          className={
            embedded
              ? "sticky bottom-0 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4"
              : "sticky bottom-0 z-10 mt-auto border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur"
          }
        >
          <div className={`flex items-center ${embedded ? "w-full justify-end gap-3" : "w-full justify-between gap-3"}`}>
            {embedded ? (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                disabled={saving}
              >
                Cancel
              </button>
            ) : null}

            <button
              type="submit"
              disabled={
                saving ||
                (showNewCustomer && !isNewCustomerValid()) ||
                validItemCount === 0 ||
                itemLimitExceeded ||
                hasInventoryViolations
              }
              className={`flex items-center justify-center gap-2 rounded-lg ${
                embedded
                  ? "bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                  : "flex-1 bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Send className="w-4 h-4" />
              {submitLabel}
            </button>

            {/* ✅ Auto-approve details icon (admin/owner only; ONLY when it WILL auto-approve) */}
            {showAutoApproveIcon && (
              <div className="relative ml-2">
                <button
                  type="button"
                  onClick={() => setShowAutoApproveTooltip((v) => !v)}
                  className="text-emerald-600 hover:text-emerald-700 focus:outline-none"
                  aria-label="Auto-approval details"
                  title="Auto-approval details"
                >
                  <CheckCircle2 className="w-5 h-5" />
                </button>

                {showAutoApproveTooltip && (
                  <div className="absolute right-0 bottom-full mb-2 w-64 p-2 rounded-md text-xs shadow-sm z-10 border bg-emerald-50 border-emerald-200 text-emerald-900">
                    <div className="font-semibold mb-1 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                      Auto-approved
                    </div>

                    <div className="mb-2">
                      ✅ This order ({formatCurrency(estimatedTotal)}) is under{" "}
                      {formatCurrency(autoApproveUnder)} and will be auto-approved.
                    </div>

                    <div className="rounded border px-2 py-2 bg-white border-emerald-200">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">Estimated Order Total:</span>
                        <span className="text-sm font-semibold">{formatCurrency(estimatedTotal)}</span>
                      </div>

                      <div className="mt-1 pt-1 border-t text-[11px] border-emerald-100">
                        • Orders under {formatCurrency(autoApproveUnder)} are auto-approved
                      </div>
                    </div>

                    <div className="absolute -bottom-1 right-2 w-2 h-2 bg-emerald-50 border-r border-b border-emerald-200 transform rotate-45"></div>
                  </div>
                )}
              </div>
            )}

            {/* Approval Info Icon (non-admin; unchanged behavior) */}
            {showApprovalIcon && (
              <div className="relative ml-2">
                <button
                  type="button"
                  onClick={() => setShowApprovalTooltip((v) => !v)}
                  className="text-blue-500 hover:text-blue-700 focus:outline-none"
                  aria-label="Approval information"
                  title="Approval information"
                >
                  <Info className="w-5 h-5" />
                </button>

                {showApprovalTooltip && (
                  <div className="absolute right-0 bottom-full mb-2 w-56 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800 shadow-sm z-10">
                    <div className="font-medium mb-0.5">Approval Required</div>
                    <p>This order will be reviewed by an admin before approval.</p>
                    <div className="absolute -bottom-1 right-2 w-2 h-2 bg-blue-50 border-r border-b border-blue-200 transform rotate-45"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
    </form>
  );

  if (embedded) {
    return renderedForm;
  }

  return (
    <div className="sample-order-form-shell flex max-h-[calc(100vh-7rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
          <Send className="w-4 h-4 text-blue-600" />
          {title}
        </h3>
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      </div>

      {renderedForm}
    </div>
  );
};

export default SampleOrderForm;
